import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../../../../components/Breadcrumb';
import Page from '../../../../components/Page';
import Pagination from '../../../../components/Pagination';
import client from '../../../../lib/feathers';

const TYPE_LABELS = {
  claim_listing: 'Claim Listing',
  access_pass: 'Access Pass',
  staff_access: 'Staff Access',
  upgrade: 'Upgrade',
  newsletter: 'Newsletter',
  custom: 'Custom',
};

const campaignStatusBadgeStyles = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  sending: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const messageStatusBadgeStyles = {
  queued: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  delivered: 'bg-green-100 text-green-800',
  opened: 'bg-emerald-100 text-emerald-800',
  clicked: 'bg-purple-100 text-purple-800',
  bounced: 'bg-red-100 text-red-800',
  spam: 'bg-red-200 text-red-900',
  unsubscribed: 'bg-yellow-100 text-yellow-800',
};

const calcRate = (numerator, denominator) => {
  if (!denominator || denominator === 0) return null;
  return `${(((numerator || 0) / denominator) * 100).toFixed(1)}%`;
};

function StatCard({ label, value, rate }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
        <dd className="mt-1 text-3xl font-semibold text-gray-900">{value}</dd>
        {rate && <dd className="mt-1 text-sm text-gray-500">{rate}</dd>}
      </div>
    </div>
  );
}

export default function CampaignDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('stats');

  // Messages tab state
  const [messages, setMessages] = useState([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [messagesPage, setMessagesPage] = useState(0);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesLimit = 25;

  // Preview tab state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Send action state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const loadCampaign = async () => {
    setLoading(true);
    setError(null);
    try {
      const [campaignResult, statsResult] = await Promise.all([
        client.service('admin/outreach-campaign').get(cid),
        client.service('admin/outreach-campaign').get(cid, { query: { $action: 'stats' } }),
      ]);
      setCampaign(campaignResult);
      setStats(statsResult);
    } catch (e) {
      console.error('Error loading campaign:', e);
      setError('Failed to load campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (p = 0) => {
    setMessagesLoading(true);
    try {
      const result = await client.service('admin/outreach-message').find({
        query: {
          campaignId: cid,
          $limit: messagesLimit,
          $skip: p * messagesLimit,
          $sort: { createdAt: -1 },
        },
      });
      setMessages(result.data);
      setMessagesTotal(result.total);
    } catch (e) {
      console.error('Error loading messages:', e);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadPreview = async () => {
    if (preview !== null) return;
    setPreviewLoading(true);
    try {
      const result = await client.service('admin/outreach-campaign').get(cid, {
        query: { $action: 'preview' },
      });
      setPreview(result);
    } catch (e) {
      console.error('Error loading preview:', e);
      setPreview({ error: 'Failed to load preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm('Are you sure you want to send this campaign? This will begin sending emails to all recipients.')) {
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const updated = await client.service('admin/outreach-campaign').patch(cid, { status: 'sending' });
      setCampaign(updated);
    } catch (e) {
      console.error('Error sending campaign:', e);
      setSendError(e.message || 'Failed to initiate send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'messages' && messages.length === 0) {
      loadMessages(0);
    }
    if (tab === 'preview') {
      loadPreview();
    }
  };

  const navigateMessagesPage = (p = 0) => {
    if (p * messagesLimit > messagesTotal || p * messagesLimit < 0) return;
    setMessagesPage(p);
    loadMessages(p);
  };

  useEffect(() => {
    loadCampaign();
  }, [cid]);

  if (loading) {
    return (
      <Page title="Campaign">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Page>
    );
  }

  if (error || !campaign) {
    return (
      <Page title="Campaign">
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-red-800">{error || 'Campaign not found.'}</p>
        </div>
      </Page>
    );
  }

  const liveStats = stats || campaign.stats || {};
  const delivered = liveStats.delivered || 0;

  const tabs = [
    { key: 'stats', label: 'Stats' },
    { key: 'messages', label: 'Messages' },
    { key: 'preview', label: 'Preview' },
  ];

  return (
    <Page title={campaign.name}>

      {/* Header */}
      <div className="pb-5 border-b border-gray-200">
        <div className="sm:flex sm:items-center sm:justify-between">
          <Breadcrumb
            elements={[
              { name: 'Outreach', href: '/admin/outreach' },
              { name: 'Campaigns', href: '/admin/outreach/campaigns' },
              { name: campaign.name },
            ]}
          />
          <div className="mt-3 sm:mt-0 sm:ml-4 flex items-center gap-3">
            {campaign.status === 'draft' && (
              <>
                <Link
                  to={`/admin/outreach/campaigns/${cid}/edit`}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Edit
                </Link>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending...' : 'Send Campaign'}
                </button>
              </>
            )}
            {campaign.status === 'sending' && (
              <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md border border-blue-200">
                <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Sending...
              </span>
            )}
          </div>
        </div>

        {sendError && (
          <div className="mt-3 rounded-md bg-red-50 p-3">
            <p className="text-sm text-red-700">{sendError}</p>
          </div>
        )}
      </div>

      {/* Campaign info */}
      <div className="mt-6 bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex items-start justify-between">
          <div>
            <h2 className="text-lg leading-6 font-semibold text-gray-900">{campaign.name}</h2>
            <p className="mt-1 text-sm text-gray-500">{TYPE_LABELS[campaign.type] || campaign.type}</p>
          </div>
          <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full capitalize ${campaignStatusBadgeStyles[campaign.status] || 'bg-gray-100 text-gray-800'}`}>
            {campaign.status}
          </span>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            {campaign.templateId && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Template ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">{campaign.templateId}</dd>
              </div>
            )}
            {campaign.fromEmail && (
              <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">From Email</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{campaign.fromEmail}</dd>
              </div>
            )}
            {campaign.createdBy && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Created By</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {`${campaign.createdBy.firstName || ''} ${campaign.createdBy.lastName || ''}`.trim() || campaign.createdBy.email}
                </dd>
              </div>
            )}
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {format(new Date(campaign.createdAt), 'MMM d, yyyy h:mm a')}
              </dd>
            </div>
            {campaign.sentAt && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Sent</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {format(new Date(campaign.sentAt), 'MMM d, yyyy h:mm a')}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-gray-500 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Stats tab */}
        {activeTab === 'stats' && (
          <div className="mt-6">
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <StatCard
                label="Total Sent"
                value={(liveStats.total || 0).toLocaleString()}
              />
              <StatCard
                label="Delivered"
                value={(liveStats.delivered || 0).toLocaleString()}
                rate={calcRate(liveStats.delivered, liveStats.total)}
              />
              <StatCard
                label="Opened"
                value={(liveStats.opened || 0).toLocaleString()}
                rate={calcRate(liveStats.opened, delivered)}
              />
              <StatCard
                label="Clicked"
                value={(liveStats.clicked || 0).toLocaleString()}
                rate={calcRate(liveStats.clicked, delivered)}
              />
              <StatCard
                label="Bounced"
                value={(liveStats.bounced || 0).toLocaleString()}
                rate={calcRate(liveStats.bounced, liveStats.total)}
              />
              <StatCard
                label="Unsubscribed"
                value={(liveStats.unsubscribed || 0).toLocaleString()}
                rate={calcRate(liveStats.unsubscribed, delivered)}
              />
            </dl>
          </div>
        )}

        {/* Messages tab */}
        {activeTab === 'messages' && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-gray-900">
                Messages ({messagesTotal.toLocaleString()} Total)
              </h3>
              <Pagination
                total={messagesTotal}
                limit={messagesLimit}
                page={messagesPage}
                navigateToPage={navigateMessagesPage}
              />
            </div>
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Listing
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delivered
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Opened
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clicks
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {messagesLoading && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        Loading messages...
                      </td>
                    </tr>
                  )}
                  {!messagesLoading && messages.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        No messages found for this campaign.
                      </td>
                    </tr>
                  )}
                  {!messagesLoading && messages.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {m.contact ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {m.contact.name || <span className="text-gray-400 italic">No name</span>}
                            </div>
                            <div className="text-sm text-gray-500">{m.contact.email}</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">{m.contactId}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {m.listing ? (
                          <Link
                            to={`/admin/listings/${m.listing.id}`}
                            className="text-sm text-gray-900 hover:text-primary hover:underline"
                          >
                            {m.listing.name}
                          </Link>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${messageStatusBadgeStyles[m.status] || 'bg-gray-100 text-gray-800'}`}>
                          {m.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {m.deliveredAt
                          ? format(new Date(m.deliveredAt), 'MMM d, yyyy')
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {m.openedAt
                          ? format(new Date(m.openedAt), 'MMM d, yyyy')
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {m.clickCount || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                total={messagesTotal}
                limit={messagesLimit}
                page={messagesPage}
                navigateToPage={navigateMessagesPage}
              />
            </div>
          </div>
        )}

        {/* Preview tab */}
        {activeTab === 'preview' && (
          <div className="mt-6">
            {previewLoading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            )}
            {!previewLoading && preview && preview.error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{preview.error}</p>
              </div>
            )}
            {!previewLoading && preview && !preview.error && (
              <div className="space-y-6">
                {/* Template variables */}
                {preview.templateVariables && Object.keys(preview.templateVariables).length > 0 && (
                  <div className="bg-white shadow sm:rounded-lg">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                      <h3 className="text-base font-medium text-gray-900">Template Variables</h3>
                      <p className="mt-1 text-sm text-gray-500">Variables used when rendering this template.</p>
                    </div>
                    <div className="px-4 py-5 sm:px-6">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        {Object.entries(preview.templateVariables).map(([key, value]) => (
                          <div key={key}>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{key}</dt>
                            <dd className="mt-1 text-sm text-gray-900 font-mono break-all">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                )}

                {/* Raw preview data */}
                {preview.html && (
                  <div className="bg-white shadow sm:rounded-lg overflow-hidden">
                    <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
                      <h3 className="text-base font-medium text-gray-900">Email Preview</h3>
                    </div>
                    <div className="p-0">
                      <iframe
                        title="Email preview"
                        srcDoc={preview.html}
                        className="w-full border-0"
                        style={{ height: '600px' }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                )}

                {/* Fallback: show raw JSON if no structured data */}
                {!preview.templateVariables && !preview.html && (
                  <div className="bg-white shadow sm:rounded-lg">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                      <h3 className="text-base font-medium text-gray-900">Preview Data</h3>
                    </div>
                    <div className="px-4 py-5 sm:px-6">
                      <pre className="text-xs text-gray-700 bg-gray-50 rounded p-4 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(preview, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

    </Page>
  );
}
